import React, { useEffect, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Mail, Trash2, Plus, GripVertical, ChevronDown, Send, CornerDownRight, ListChecks, CalendarDays, Phone, X, MessageCircle } from "lucide-react";
import EmailBodyInput from "../../EmailBodyInput";
import AIEmailModal from "../../AIEmailModal";
import { VARIABLES, FORM_LINK_VARIABLE, stepTitle, minDelayFor, stripHtml, generateEmailDraft, isCallStep, isWhatsAppStep, isEmailStep, renderWaPreview, fetchWhatsAppTemplates } from "../sequenceBuilderApi";
import whatsappLogo from "../../../../assets/whatsapp.svg";
import "../gradientAccents.css";

const startLabel = (t) => (t === "next_business_day" ? "Next business day" : "Immediately");

/**
 * Wizard step — the day-by-day outreach cadence, as a master-detail: a reorderable step rail on
 * the left and the selected step's editor on the right. Each step is one channel: email,
 * WhatsApp, or (on a screening outreach) an AI call. Owns only view-local state (which step is selected + the
 * editor field refs); all step mutations are lifted to the shell so they stay draft-persistable.
 */
const StepSequence = ({
    steps,
    onUpdateStep,
    onAddStep,
    onInsertAt,
    onDeleteStep,
    onReorder,
    startTiming,
    onStartTimingChange,
    gmailEmail,
    accountLoading,
    onConnectMailbox,
    includeScreening,
    onEnableScreening,
    onTestStep,
    outreachName,
    senderName,
}) => {
    const [activeIdx, setActiveIdx] = useState(0);
    const [addOpen, setAddOpen] = useState(false);
    // Approved Meta templates — a WhatsApp step picks one rather than authoring copy.
    const [waTemplates, setWaTemplates] = useState([]);
    const focusedFieldRef = useRef("body");
    const subjectRef = useRef(null);
    const bodyRef = useRef(null);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiFeedback, setAiFeedback] = useState(null);

    // Keep the selection valid as steps are added / removed / reordered.
    useEffect(() => {
        setActiveIdx((idx) => Math.min(Math.max(idx, 0), Math.max(steps.length - 1, 0)));
    }, [steps.length]);

    // Fetched once for the whole wizard — the picker needs them before a step turns WhatsApp.
    useEffect(() => {
        let alive = true;
        fetchWhatsAppTemplates()
            .then((t) => alive && setWaTemplates(t))
            .catch(() => alive && setWaTemplates([]));
        return () => {
            alive = false;
        };
    }, []);

    const activeStep = steps[activeIdx];
    const variables = includeScreening ? [...VARIABLES, FORM_LINK_VARIABLE] : VARIABLES;
    // Immediate send counts as Day 1, so every step's day number is its delayDays offset + 1.
    const totalDays = steps[steps.length - 1].delayDays + 1;

    const handleDragEnd = ({ source, destination }) => {
        if (!destination || source.index === destination.index) return;
        onReorder(source.index, destination.index);
        setActiveIdx(destination.index);
    };

    const insertVariable = (token) => {
        if (focusedFieldRef.current === "subject") {
            const el = subjectRef.current;
            if (!el) return;
            const start = el.selectionStart ?? el.value.length;
            const end = el.selectionEnd ?? el.value.length;
            const cur = activeStep?.subject || "";
            onUpdateStep(activeIdx, "subject", cur.slice(0, start) + token + cur.slice(end));
            setTimeout(() => {
                el.focus();
                el.setSelectionRange(start + token.length, start + token.length);
            }, 0);
        } else {
            const quill = bodyRef.current?.getEditor();
            if (!quill) return;
            const range = quill.getSelection(true);
            const index = range ? range.index : quill.getLength();
            quill.insertText(index, token, "user");
            quill.setSelection(index + token.length, 0);
        }
    };

    const addAndSelect = (channel = "email") => {
        onAddStep(channel);
        setActiveIdx(steps.length); // the appended step's index
        setAddOpen(false);
    };

    const insertAfter = (idx) => {
        onInsertAt(idx + 1);
        setActiveIdx(idx + 1);
    };

    const handleGenerateWithAI = async (instruction) => {
        setAiBusy(true);
        setAiFeedback(null);
        try {
            const result = await generateEmailDraft({
                instruction,
                senderName,
                outreachName,
                includeScreening,
                // Position in the cadence decides whether this reads as a first touch or a
                // follow-up, so the backend is told where in the ladder this step sits.
                stepNumber: activeIdx + 1,
                totalSteps: steps.length,
            });
            if (!result.usable) {
                setAiFeedback(result.message);
                return;
            }
            onUpdateStep(activeIdx, "body", result.body);
            // A subject the recruiter already wrote is theirs — the draft only fills a blank one.
            if (result.subject && !activeStep?.subject?.trim()) {
                onUpdateStep(activeIdx, "subject", result.subject);
            }
            setAiOpen(false);
        } catch (e) {
            setAiFeedback(
                e.response?.data?.detail || "Couldn't generate the email just now. Please try again."
            );
        } finally {
            setAiBusy(false);
        }
    };

    return (
        <div className="flex flex-col">
            <h1 className="text-lg font-light text-left dark:text-white text-textPrimary mb-6">
                Set the timing and content for each step in your outreach.
            </h1>

            {/* Only a concern if something actually sends mail — a call-only outreach needs no mailbox. */}
            {!accountLoading && !gmailEmail && steps.some(isEmailStep) && (
                <div className="shrink-0 mb-3 flex items-center justify-between gap-3 px-4 py-2.5 dark:bg-red-500/10 bg-red-50 dark:border-red-500/30 border border-red-200 rounded-2xl">
                    <span className="text-sm dark:text-red-300 text-red-700">
                        No sender email connected — connect a mailbox so these emails can send.
                    </span>
                    <button
                        onClick={onConnectMailbox}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-white shrink-0"
                        style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                    >
                        Connect mailbox
                    </button>
                </div>
            )}

            {/* One panel, master-detail. No internal divider or header rules — the rail is set
                apart only by a soft tint, and the selected step's card bleeds over that tint's
                edge so it merges with the (white) editor surface, tab-style. */}
            <div className="flex border border-gray-200 dark:border-gray-700/50 rounded-xl dark:glass-effect bg-bgPrimary overflow-hidden dark:shadow-lg shadow-sm">
                {/* Step rail */}
                <div className="w-80 shrink-0 flex flex-col bg-gray-50 dark:bg-white/[0.03]">
                    <div className="h-14 flex items-center px-6 shrink-0">
                        {/* Quiet section labels (same treatment as "Sequence Stats" below) — the
                            rail is navigation, so the loud title belongs to the editor side. */}
                        <h3 className="text-base font-medium dark:text-gray-500 text-textPrimary">Outreach Steps</h3>
                    </div>

                    <div className="px-6 pb-4">
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="seqsteps">
                                {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col gap-3">
                                        {steps.map((step, idx) => (
                                            <Draggable key={step._uid || `step-${idx}`} draggableId={step._uid || `step-${idx}`} index={idx}>
                                                {(prov, snapshot) => (
                                                    <div
                                                        ref={prov.innerRef}
                                                        {...prov.draggableProps}
                                                        onClick={() => setActiveIdx(idx)}
                                                        className={`group relative border cursor-pointer transition-all ${
                                                            snapshot.isDragging
                                                                ? "rounded-xl shadow-lg seq-gradient-border bg-bgPrimary dark:bg-[#1B1524]"
                                                                : activeIdx === idx
                                                                // The tab: -mr-6 cancels the rail's px-6 so the card runs flush
                                                                // to the rail edge, and its fill — the same white as the editor
                                                                // — continues straight into the pane. No outline: a stroke would
                                                                // have to stop dead at the seam, which is what reads as broken.
                                                                // The notch the card cuts out of the tint is the whole effect.
                                                                // The wash fades left→right into plain white, i.e. into the
                                                                // editor's own surface — the card visibly runs out into the
                                                                // pane instead of stopping at an edge.
                                                                ? "rounded-l-xl rounded-r-none -mr-6 border-transparent bg-bgPrimary bg-gradient-to-r from-[#FF60B6]/10 via-[#2960FD]/5 to-transparent shadow-[0_1px_3px_rgba(16,24,40,0.06)]"
                                                                : "rounded-xl border-transparent bg-transparent hover:bg-bgPrimary hover:border-gray-200 dark:hover:border-gray-600"
                                                        }`}
                                                    >
                                                        {/* Gradient tick on the selected card's leading edge — a closed shape,
                                                            so nothing dangles at the seam the way an open outline did. */}
                                                        {activeIdx === idx && !snapshot.isDragging && (
                                                            <span
                                                                aria-hidden
                                                                className="seq-gradient-fill absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full"
                                                            />
                                                        )}
                                                        {/* Selected card bleeds 24px past the rail, so its own right padding
                                                            grows to keep the delete button inside the rail, not floating in
                                                            the editor pane. */}
                                                        <div className={`flex items-center gap-3 py-3 pl-4 ${activeIdx === idx && !snapshot.isDragging ? "pr-10" : "pr-4"}`}>
                                                            <span
                                                                {...prov.dragHandleProps}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-gray-300 dark:text-gray-600 hover:text-gray-500 cursor-grab active:cursor-grabbing shrink-0"
                                                                title="Drag to reorder"
                                                            >
                                                                <GripVertical className="w-4 h-4" />
                                                            </span>
                                                            <span
                                                                className={`w-7 h-7 rounded-full flex items-center justify-center text-[13px] font-medium shrink-0 ${
                                                                    activeIdx === idx
                                                                        ? "seq-gradient-fill text-white"
                                                                        : "bg-bgPrimary dark:bg-white/10 border border-gray-200 dark:border-transparent text-gray-500 dark:text-gray-300"
                                                                }`}
                                                            >
                                                                {idx + 1}
                                                            </span>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-[13px] dark:text-gray-400 text-textSecondary truncate">
                                                                    {idx === 0 ? startLabel(startTiming) : `Day ${step.delayDays + 1}`}
                                                                </p>
                                                                {/* Channel icon so a call is scannable
                                                                    down the rail, not just readable. */}
                                                                <p className={`text-base font-medium truncate flex items-center gap-1.5 ${activeIdx === idx ? "seq-gradient-text" : "dark:text-white text-textPrimary"}`}>
                                                                    {isCallStep(step) ? (
                                                                        <Phone className={`w-3.5 h-3.5 shrink-0 ${activeIdx === idx ? "text-[#2960FD]" : "text-gray-400 dark:text-gray-500"}`} />
                                                                    ) : isWhatsAppStep(step) ? (
                                                                        <MessageCircle className={`w-3.5 h-3.5 shrink-0 ${activeIdx === idx ? "text-green-500" : "text-gray-400 dark:text-gray-500"}`} />
                                                                    ) : (
                                                                        <Mail className={`w-3.5 h-3.5 shrink-0 ${activeIdx === idx ? "text-[#FF60B6]" : "text-gray-400 dark:text-gray-500"}`} />
                                                                    )}
                                                                    {stepTitle(step)}
                                                                </p>
                                                                {/* <p className="text-[13px] dark:text-gray-400 text-textSecondary truncate">
                                                                    {step.subject?.trim() || "No subject yet"}
                                                                </p> */}
                                                            </div>
                                                            {steps.length > 1 && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); onDeleteStep(idx); }}
                                                                    className="opacity-0 group-hover:opacity-100 text-gray-300 dark:text-gray-600 hover:text-red-400 transition-all shrink-0"
                                                                    title="Delete step"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        {/* Add Step asks which channel first — the two produce completely different
                            editors, so choosing up front beats adding an email step and converting
                            it. The choices expand in place rather than floating: the panel around
                            this rail is overflow-hidden, which would clip a dropdown. */}
                        {addOpen ? (
                            <div className="mt-3 p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-bgPrimary dark:bg-[#1B1524] flex flex-col gap-1">
                                <div className="flex items-center justify-between pl-2.5 pr-1 pt-0.5 pb-1">
                                    <span className="text-[11px] uppercase tracking-wide dark:text-gray-500 text-gray-400">
                                        Add a step
                                    </span>
                                    <button
                                        onClick={() => setAddOpen(false)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded"
                                        title="Cancel"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                <button
                                    onClick={() => addAndSelect("email")}
                                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
                                >
                                    <Mail className="w-4 h-4 shrink-0 text-[#FF60B6]" />
                                    <span className="flex flex-col min-w-0">
                                        <span className="text-[13px] font-medium dark:text-white text-textPrimary">Email</span>
                                        <span className="text-[11px] dark:text-gray-400 text-textSecondary">Write a message to send</span>
                                    </span>
                                </button>
                                <button
                                    onClick={() => addAndSelect("whatsapp")}
                                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
                                >
                                    <MessageCircle className="w-4 h-4 shrink-0 text-green-500" />
                                    <span className="flex flex-col min-w-0">
                                        <span className="text-[13px] font-medium dark:text-white text-textPrimary">WhatsApp</span>
                                        <span className="text-[11px] dark:text-gray-400 text-textSecondary">Send an approved template</span>
                                    </span>
                                </button>
                                <button
                                    onClick={() => (includeScreening ? addAndSelect("call") : onEnableScreening?.())}
                                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
                                >
                                    <Phone className="w-4 h-4 shrink-0 text-[#2960FD]" />
                                    <span className="flex flex-col min-w-0">
                                        <span className="text-[13px] font-medium dark:text-white text-textPrimary">AI Call</span>
                                        <span className="text-[11px] dark:text-gray-400 text-textSecondary">
                                            {includeScreening
                                                ? "An agent asks your screening questions"
                                                : "Needs screening questions — turn them on"}
                                        </span>
                                    </span>
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setAddOpen(true)}
                                className="seq-gradient-hover w-full flex items-center justify-center gap-2 py-2.5 mt-3 text-[13px] dark:text-white text-textSecondary border border-dashed border-gray-300 dark:border-gray-600 rounded-full transition-all duration-200"
                            >
                                <Plus className="w-4 h-4" /> Add Step
                            </button>
                        )}
                    </div>

                    {/* Stats sit at the foot of the rail (mt-auto) — no rule above them; the tiles
                        are white on the tinted rail, which is separation enough. */}
                    <div className="shrink-0 mt-auto px-6 py-5">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-xl bg-bgPrimary dark:bg-white/5 border border-gray-100 dark:border-gray-700/50 px-4 py-3 flex items-center gap-2">
                                <ListChecks className="w-4 h-4 shrink-0 text-[#FF60B6]" />
                                <span className="text-lg font-medium dark:text-white text-textPrimary">{steps.length}</span>
                                <span className="text-sm dark:text-gray-400 text-textSecondary">Steps</span>
                            </div>
                            <div className="rounded-xl bg-bgPrimary dark:bg-white/5 border border-gray-100 dark:border-gray-700/50 px-4 py-3 flex items-center gap-2">
                                <CalendarDays className="w-4 h-4 shrink-0 text-[#2960FD]" />
                                <span className="text-lg font-medium dark:text-white text-textPrimary">{totalDays}</span>
                                <span className="text-sm dark:text-gray-400 text-textSecondary">Days</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Selected step editor */}
                <div className="flex-1 min-w-0 flex flex-col">
                    {!activeStep ? (
                        <div className="flex items-center justify-center text-sm dark:text-gray-400 text-textSecondary py-16">No step selected.</div>
                    ) : (
                        <>
                            {/* The header names the same step number and timing the selected card
                                shows, so the two halves are visibly the same step rather than two
                                unrelated panes. */}
                            <div className="h-14 flex items-center gap-2.5 px-6 shrink-0">
                                <h3 className="text-base font-medium dark:text-white text-textPrimary">Step {activeIdx + 1}</h3>
                                <span className="text-gray-300 dark:text-gray-600">·</span>
                                <span className="text-base dark:text-gray-400 text-textSecondary">
                                    {activeIdx === 0 ? startLabel(startTiming) : `Day ${activeStep.delayDays + 1}`}
                                </span>
                                <div className="flex-1" />
                                {/* Preview & test sends a real email — only email steps have one. */}
                                {isEmailStep(activeStep) && (
                                    <button
                                        onClick={() => onTestStep(activeStep)}
                                        className="flex items-center gap-2 text-[13px] dark:text-white text-textSecondary border border-gray-200 dark:border-gray-600 rounded-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-white/10 transition-all duration-200"
                                    >
                                        Preview &amp; test
                                    </button>
                                )}
                            </div>

                            <div className="px-6 py-4 flex flex-col gap-4">
                                {/* Timing */}
                                <div className="flex items-center gap-2">
                                    <span className="text-sm dark:text-gray-400 text-textSecondary w-20 shrink-0">{activeIdx === 0 ? "Start" : "Wait Time"}</span>
                                    {activeIdx === 0 ? (
                                        <div className="relative">
                                            <select
                                                value={startTiming}
                                                onChange={(e) => onStartTimingChange(e.target.value)}
                                                className="appearance-none text-sm dark:text-[#CCCCCC] text-textSecondary bg-bgPrimary dark:bg-[#1B1524] border border-gray-200 dark:border-gray-600 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-[#2960FD] cursor-pointer"
                                            >
                                                <option value="immediately">Immediately</option>
                                                <option value="next_business_day">Next business day</option>
                                            </select>
                                            <ChevronDown className="w-3.5 h-3.5 dark:text-gray-400 text-textSecondary absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                min={minDelayFor(steps, activeIdx)}
                                                value={activeStep.delayDays}
                                                onChange={(e) => {
                                                    const min = minDelayFor(steps, activeIdx);
                                                    const val = Math.max(min, Number(e.target.value) || min);
                                                    onUpdateStep(activeIdx, "delayDays", val);
                                                }}
                                                className="w-16 text-sm dark:text-[#CCCCCC] text-textSecondary bg-bgPrimary dark:bg-transparent border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#2960FD]"
                                            />
                                            <span className="text-sm dark:text-gray-400 text-textSecondary">Day{activeStep.delayDays === 1 ? "" : "s"}</span>
                                            <span className="text-sm dark:text-gray-400 text-textSecondary">Send @</span>
                                            <input
                                                type="time"
                                                value={activeStep.sendAt}
                                                onChange={(e) => onUpdateStep(activeIdx, "sendAt", e.target.value)}
                                                className="w-24 text-[13px] dark:text-[#CCCCCC] text-textSecondary bg-bgPrimary dark:bg-transparent border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1 outline-none focus:border-[#2960FD] dark:[color-scheme:dark]"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Channel — AI call is offered only on a screening outreach, since
                                    the agent's questions come from the screening form. */}
                                <div className="flex items-center gap-2">
                                    <span className="text-sm dark:text-gray-400 text-textSecondary w-20 shrink-0">Channel</span>
                                    <div className="flex gap-2">
                                        {[
                                            { value: "email", label: "Email", Icon: Mail },
                                            { value: "whatsapp", label: "WhatsApp", Icon: MessageCircle },
                                            { value: "call", label: "AI Call", Icon: Phone },
                                        ].map(({ value, label, Icon }) => {
                                            const selected = (activeStep.channel || "email") === value;
                                            // AI Call needs a screening form for its questions. Rather
                                            // than dead-end on a disabled button, offer the fix.
                                            const needsScreening = value === "call" && !includeScreening;
                                            return (
                                                <button
                                                    key={value}
                                                    onClick={() =>
                                                        needsScreening
                                                            ? onEnableScreening?.()
                                                            : onUpdateStep(activeIdx, "channel", value)
                                                    }
                                                    title={
                                                        needsScreening
                                                            ? "Turn on screening questions to use an AI call"
                                                            : undefined
                                                    }
                                                    className={`flex items-center gap-1.5 text-[13px] px-4 py-2 rounded-full border transition-all duration-200 ${
                                                        selected
                                                            ? "seq-gradient-fill text-white border-transparent"
                                                            : "seq-gradient-hover border-gray-200 dark:border-gray-600 dark:text-white text-textSecondary"
                                                    }`}
                                                >
                                                    <Icon className="w-3.5 h-3.5" />
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {!includeScreening && (
                                        <button
                                            onClick={() => onEnableScreening?.()}
                                            className="text-[13px] text-[#2960FD] hover:underline"
                                        >
                                            Turn on screening questions to use AI Call
                                        </button>
                                    )}
                                </div>

                                {isWhatsAppStep(activeStep) ? (
                                    /* WhatsApp copy must come from a template Meta has already
                                       approved, so this picks one instead of composing. */
                                    <div className="flex flex-col gap-3">
                                        <span className="text-[13px] text-green-600 font-medium flex items-center gap-1.5">
                                            <img src={whatsappLogo} alt="" className="w-3.5 h-3.5" />
                                            WhatsApp template
                                        </span>
                                        {waTemplates.length === 0 ? (
                                            <p className="text-[13px] dark:text-gray-400 text-textSecondary">
                                                No WhatsApp templates registered yet — a developer can add one from
                                                the Outreach home page ("Dev: Register WhatsApp Template").
                                            </p>
                                        ) : (
                                            <>
                                                <div className="relative w-full max-w-md">
                                                    <select
                                                        value={activeStep.whatsappTemplateId || ""}
                                                        onChange={(e) => {
                                                            const id = e.target.value || null;
                                                            onUpdateStep(activeIdx, "whatsappTemplateId", id);
                                                            // Carried alongside the id so the review card can
                                                            // name the template without refetching.
                                                            onUpdateStep(
                                                                activeIdx,
                                                                "whatsappTemplateName",
                                                                waTemplates.find((t) => t.id === id)?.name || null
                                                            );
                                                        }}
                                                        className="appearance-none w-full text-sm dark:text-[#CCCCCC] text-textSecondary bg-bgPrimary dark:bg-[#1B1524] border border-gray-200 dark:border-gray-600 rounded-lg pl-3 pr-8 py-2 outline-none focus:border-green-400 cursor-pointer"
                                                    >
                                                        <option value="">Select a template…</option>
                                                        {waTemplates.map((t) => (
                                                            <option key={t.id} value={t.id}>{t.name}</option>
                                                        ))}
                                                    </select>
                                                    <ChevronDown className="w-3.5 h-3.5 dark:text-gray-400 text-textSecondary absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                                                </div>
                                                {activeStep.whatsappTemplateId && (
                                                    <div className="bg-[#e5ddd5] rounded-xl px-3 py-3 flex justify-start max-w-md">
                                                        <div className="bg-[#d9fdd3] rounded-lg rounded-tl-none px-3 py-2 text-sm text-gray-800 max-w-[85%] shadow-sm whitespace-pre-wrap">
                                                            {renderWaPreview(
                                                                waTemplates.find((t) => t.id === activeStep.whatsappTemplateId)
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ) : isCallStep(activeStep) ? (
                                    /* A call step has no copy to write — the agent asks the screening
                                       questions set up in the previous wizard step. */
                                    <div className="flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-700/50 bg-gray-50 dark:bg-white/[0.03] px-4 py-3.5">
                                        <Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#2960FD]" />
                                        <div className="flex flex-col gap-1">
                                            <p className="text-sm font-medium dark:text-white text-textPrimary">
                                                An AI agent calls the candidate
                                            </p>
                                            <p className="text-[13px] dark:text-gray-400 text-textSecondary">
                                                It asks your screening questions and records the answers automatically.
                                                Candidates without a phone number are skipped.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                <>
                                {/* From */}
                                <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-700/50 pb-3">
                                    <span className="text-sm dark:text-gray-400 text-textSecondary w-20 shrink-0">From</span>
                                    <span className="text-sm dark:text-[#CCCCCC] text-textSecondary">{gmailEmail || "No email connected"}</span>
                                </div>

                                {/* Subject */}
                                <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-700/50 pb-3">
                                    <span className="text-sm dark:text-gray-400 text-textSecondary w-20 shrink-0">Subject</span>
                                    <input
                                        ref={subjectRef}
                                        type="text"
                                        value={activeStep.subject}
                                        onChange={(e) => onUpdateStep(activeIdx, "subject", e.target.value)}
                                        onFocus={() => { focusedFieldRef.current = "subject"; }}
                                        placeholder="Email subject line"
                                        className="flex-1 text-sm dark:text-[#CCCCCC] text-textSecondary outline-none bg-transparent"
                                    />
                                </div>

                                {/* Variables */}
                                <div className="flex flex-wrap gap-2">
                                    {variables.map((v) => (
                                        <button
                                            key={v.token}
                                            onClick={() => insertVariable(v.token)}
                                            className="seq-gradient-hover text-[13px] px-4 py-2 rounded-full border border-gray-200 dark:border-gray-600 dark:text-white text-textSecondary transition-all duration-200"
                                        >
                                            {v.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Body — remounts per step so the editor always reflects the selected step */}
                                <div onFocus={() => { focusedFieldRef.current = "body"; }}>
                                    <EmailBodyInput
                                        key={activeIdx}
                                        ref={bodyRef}
                                        value={activeStep.body}
                                        onChange={(val) => onUpdateStep(activeIdx, "body", val)}
                                        placeholder="Write your email…"
                                        onGenerateWithAI={() => { setAiFeedback(null); setAiOpen(true); }}
                                        aiBusy={aiBusy}
                                    />
                                </div>
                                </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <AIEmailModal
                isOpen={aiOpen}
                onClose={() => { setAiOpen(false); setAiFeedback(null); }}
                onGenerate={handleGenerateWithAI}
                busy={aiBusy}
                feedback={aiFeedback}
                stepNumber={activeIdx + 1}
                currentSubject={activeStep?.subject || ""}
                currentPreview={stripHtml(activeStep?.body).slice(0, 120)}
                hasContent={!!(activeStep?.subject?.trim() || stripHtml(activeStep?.body))}
            />
        </div>
    );
};

export default StepSequence;
